import { ObjectType } from "../types/tableTypes";

// Every Pricing-adjustment row label (e.g. "Price Adj. - FICO") contains a
// literal "." — and pricingAdjustmentOverrides is saved/loaded as an object
// keyed by that label. Confirmed in practice: "Price Adj. - Pre-payment
// Penalty Term" round-trips through save/load as
// { "Price Adj": { " - Pre-payment Penalty Term": value } } — some dot-path-
// aware layer in the save/load round trip (almost certainly a MongoDB-style
// store, where "." in a field name is a reserved nested-path separator)
// silently explodes it. Swapping "." for U+2024 ONE DOT LEADER (visually a
// dot, never produced by real user input) right before saving — and back
// right after loading — sidesteps this without touching how the rest of the
// app uses these labels as keys (display, audit paths, draft state, the
// guideline-API override injection all keep using the real "." label).
const PRICING_OVERRIDE_KEY_DOT_PLACEHOLDER = "․";

export const encodePricingOverrideKey = (label: string): string =>
  label.replace(/\./g, PRICING_OVERRIDE_KEY_DOT_PLACEHOLDER);

export const decodePricingOverrideKey = (key: string): string =>
  key.replace(new RegExp(PRICING_OVERRIDE_KEY_DOT_PLACEHOLDER, "g"), ".");

export const encodePricingAdjustmentOverrides = (
  overrides: Record<string, string> | undefined | null,
): Record<string, string> | null => {
  if (!overrides || !Object.keys(overrides).length) return null;
  return Object.fromEntries(
    Object.entries(overrides).map(([label, value]) => [encodePricingOverrideKey(label), value]),
  );
};

export const decodePricingAdjustmentOverrides = (
  raw: Record<string, string> | undefined | null,
): Record<string, string> => {
  if (!raw) return {};
  return Object.fromEntries(
    Object.entries(raw).map(([key, value]) => [decodePricingOverrideKey(key), value]),
  );
};

// Same save/load round trip as pricingAdjustmentOverrides above, minus the
// key-encoding step — leverageAdjustedMaxOverrides is keyed by column header
// label ("ltvAsIs" / "ltc" / "ltvARV" / "lttc"), none of which ever contain a
// ".", so there's no dot-path collision to guard against. Kept as its own
// pair of functions (rather than reusing the pricing ones) so the "return
// null for an empty object" clearing behavior stays explicit at the call
// site in handleSaveApplication, same as pricing.
export const encodeLeverageAdjustedMaxOverrides = (
  overrides: Record<string, string> | undefined | null,
): Record<string, string> | null => {
  if (!overrides || !Object.keys(overrides).length) return null;
  return { ...overrides };
};

export const decodeLeverageAdjustedMaxOverrides = (
  raw: Record<string, string> | undefined | null,
): Record<string, string> => {
  if (!raw) return {};
  return { ...raw };
};

const pricingLabelMappings = {
  borrowerFico: "Borrower FICO Pricing Bucket",
  // actualLeverage: "Actual Leverage - LTV/LTC/LTP",
  baseFicoLeverageBucket: "Base FICO / Leverage Adjusted Rate",
  prePaymentPenalty: "Rate Adj. - Prepayment Penalty",
  interestOnlyRate: "Rate Adj. - Interest Only",
  dscrRate: "Rate Adj. - DSCR",
  shortTermRentalRate: "Rate Adj. - Short Term Rental",
  cashOutRefinanceRate: "Rate Adj. - Cash Out Refinance",
  propertyTypeRate: "Rate Adj. - Property Type",
  loanBalanceRate: "Rate Adj. - Loan Balance",
  amortizationRate: "Rate Adj. - ARM",
  leveragePricingBucket: "Leverage Pricing Bucket",
  loanBalanceBucket: "Loan Balance Bucket",
};

export const labelFieldPricingMapping = [
  {
    label: "Final Price Adjustment",
    field: "totalPriceAdjustment",
    // Total Pricing Adjustments is a decimal number, not a percentage — no
    // "%" suffix (see getPricingData's "decimal" branch in utils/Common.ts).
    fieldType: "decimal",
    infoContent: `toorakProceeds.totalPriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Borrower FICO Pricing Bucket",
    field: "borrowerFico",
    fieldType: "string",
    infoContent: `toorakProceeds.borrowerFico`,
    convertToPercentage: false,
  },
  // {
  //   label: "Actual Leverage - LTV/LTC/LTP",
  //   field: "actualLeverage",
  //   fieldType: "percentage",
  //   infoContent: `toorakProceeds.actualLeverage`,
  //   convertToPercentage: true,
  // },
  {
    label: "Price Adj. - FICO",
    field: "ficoPriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.ficoPriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - Pre-payment Penalty Term",
    field: "penaltyTermPriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.penaltyTermPriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - Loan Exceptions",
    field: "loanExceptionsPriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.loanExceptionsPriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - DSCR",
    field: "dscrPriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.dscrPriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - Loan Term",
    field: "shortTermRentalRate",
    fieldType: "string",
    infoContent: `loanPricing.shortTermRentalRate`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - Loan Purpose",
    field: "loanPurposePriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.loanPurposePriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - Property Type",
    field: "propertyTypePriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.propertyTypePriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - Loan Balance",
    field: "loanBalancePriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.loanBalancePriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - Cross Collateralized",
    field: "crossCollaterizedAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.crossCollaterizedAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - Amortization",
    field: "amortizationPriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.amortizationPriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Leverage Pricing Bucket",
    field: "leveragePricingBucket",
    fieldType: "string",
    infoContent: `loanPricing.leveragePricingBucket`,
    convertToPercentage: false,
  },
  {
    label: "Loan Balance Bucket",
    field: "loanBalanceBucket",
    fieldType: "string",
    infoContent: `loanPricing.loanBalanceBucket`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. - Tier state",
    field: "propertyLocationPriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.loanBalanceBucket`,
    convertToPercentage: false,
  },
  {
    label: "FC/SS/DIL/BK7 36 - 47mo",
    field: "creditEventPriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.creditEventPriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Price Adj. FN/NPRA",
    field: "loanUserTypePriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.loanUserTypePriceAdjustment`,
    convertToPercentage: false,
  },
  {
    label: "Florida State Adjustment",
    field: "statePriceAdjustment",
    fieldType: "string",
    infoContent: `loanPricing.statePriceAdjustment`,
    convertToPercentage: false,
  },
];

// Column-header-label -> backend field mapping for the "Adjusted Max" row of
// the Leverages card, keyed by loanStage (the same string
// getLeverageTableBody switches on in src/utils/Common.ts). Each entry names
// the exact resultObject section/field an override for that column must land
// under so a saved edit reaches the guideline API the same way the row's
// live value was originally read from — no separate lookup for "what does
// this column mean for this loan type" should ever exist outside this table.
export const labelFieldLeverageMapping: Record<
  string,
  { column: string; section: string; field: string }[]
> = {
  Rehab: [
    { column: "ltvAsIs", section: "finalLeverage", field: "afterAdjustmentMaxLTVAsIs" },
    { column: "ltvARV", section: "finalLeverage", field: "afterAdjustmentMaxARV" },
    { column: "ltc", section: "finalLeverage", field: "afterAdjustmentMaxLTC" },
  ],
  "Fix & Flip": [
    { column: "ltvAsIs", section: "finalLeverage", field: "afterAdjustmentMaxLTVAsIs" },
    { column: "ltvARV", section: "finalLeverage", field: "afterAdjustmentMaxARV" },
    { column: "ltc", section: "finalLeverage", field: "afterAdjustmentMaxLTC" },
  ],
  Stabilized: [
    { column: "ltvAsIs", section: "finalLeverage", field: "afterAdjustmentMaxLTVAsIs" },
    { column: "ltvARV", section: "finalLeverage", field: "afterAdjustmentMaxARV" },
    { column: "ltc", section: "finalLeverage", field: "afterAdjustmentMaxLTC" },
  ],
  MultiFamily: [
    { column: "ltvAsIs", section: "finalLeverage", field: "afterAdjustmentMaxLTVAsIs" },
    { column: "ltvARV", section: "finalLeverage", field: "afterAdjustmentMaxARV" },
    { column: "ltc", section: "finalLeverage", field: "afterAdjustmentMaxLTC" },
    { column: "lttc", section: "finalLeverage", field: "afterAdjustmentMaxLTTC" },
  ],
  "Ground Up": [
    { column: "ltvAsIs", section: "finalLeverage", field: "afterAdjustmentMaxLTVAsIs" },
    { column: "ltvARV", section: "finalLeverage", field: "afterAdjustmentMaxARV" },
    { column: "ltc", section: "finalLeverage", field: "afterAdjustmentMaxLTC" },
    { column: "lttc", section: "finalLeverage", field: "afterAdjustmentMaxLTTC" },
  ],
  DSCR: [
    { column: "ltvAsIs", section: "toorakProceeds", field: "toorakAdjustedAsIsLtv" },
    { column: "ltc", section: "toorakProceeds", field: "toorakAdjustedAsIsLtc" },
  ],
};

export const getLeverageAdjustedMaxFieldMapping = (
  loanStage?: string,
): { column: string; section: string; field: string }[] =>
  labelFieldLeverageMapping[loanStage ?? "DSCR"] ?? labelFieldLeverageMapping.DSCR;

// leverageAdjustedMaxOverrides is stored/displayed as "<number> %" (see
// LeverageTable.tsx's percentage input), but the guideline API's resultObject
// fields this maps onto (toorakProceeds.toorakAdjustedAsIsLtv,
// finalLeverage.afterAdjustmentMaxLTVAsIs, ...) are plain numeric percentages
// everywhere else the rules engine reads them from — sending "23 %" there
// instead of 23 would be the one override value in the whole payload the
// engine can't parse. Used only when building that request body (see
// getRequestBodyForGuidelines in utils/v2/common.ts); the stored/displayed
// "<number> %" string itself is untouched everywhere else.
export const parseLeverageOverridePercentage = (value: string): number => {
  // Linear-time equivalent of /\s*%\s*$/ (avoids the ReDoS-prone pattern):
  // trim trailing whitespace, drop a single trailing '%', then trim again.
  const str = String(value);
  const trimmed = str.trimEnd();
  const withoutPercent = trimmed.endsWith("%") ? trimmed.slice(0, -1).trimEnd() : str;
  return Number(withoutPercent);
};

export const labelFieldBridgePriceMapping = [
  {
    label: "Location Adjustments",
    field: "locationAdjustment",
    fieldType: "percentage",
    infoContent: `toorakInterests.locationAdjustment`,
    isVisible: (isGroundUp: boolean) => true,
  },
  {
    label: "Loan Purpose Adjustments",
    field: "loanPurposeAdjustment",
    fieldType: "percentage",
    infoContent: `toorakInterests.loanPurposeAdjustment`,
    isVisible: (isGroundUp: boolean) => true,
  },
  {
    label: "Ground Up / Multifamily Adjustment",
    field: "groundUpAdjustment",
    fieldType: "percentage",
    infoContent: `toorakInterests.groundUpAdjustment`,
    isVisible: (isGroundUp: boolean) => true,
  },
  {
    label: "Rehab Type Adjustments",
    field: "rehabTypeAdjustment",
    fieldType: "percentage",
    infoContent: `toorakInterests.rehabTypeAdjustment`,
    isVisible: (isGroundUp: boolean) => !isGroundUp,
  },
  {
    label: "Leverage Rate Adjustments",
    field: "leverageRateAdjustment",
    fieldType: "percentage",
    infoContent: `toorakInterests.leverageRateAdjustment`,
    isVisible: (isGroundUp: boolean) => true,
  },
  {
    label: "Term Adjustment",
    field: "termAdjustment",
    fieldType: "percentage",
    infoContent: `toorakInterests.termAdjustment`,
    isVisible: (isGroundUp: boolean) => true,
  },
];

export enum SortOrder {
  ascending = "ASC",
  descending = "DESC",
}
export const labelFieldAdjustmentMapping: ObjectType = {
  medianFico: "Median FICO",
  foreignNational:"Foreign National",
  medianHomePriceDecline: "Median Home Price Decline",
  propertyValueVSMedianZipCodeValue: "Property Value vs Median Zip Code Value",
  equityPledge: "Equity Pledge",
  brookylnQueensBronx: "Brooky ln Queens Bronx",
  manhatten: "Manhattan",
  sanFransiscoPeninsula:"SF Peninsula",
  newarkandAdjSuburbsPaterson: "Newark and Adj Suburbs, Paterson",
  maxLoanSize: "Max Loan Size",
  mixedUseAdjustment: "Mixed Use",
  valuePerUnitAdjustment: "ARV >= $40 K and <= $50 K LTV",
  manhattenAdjustment:"Property in Manhattan",
  sanFranPatersonNewarkAdjustment: "Property in San Francisco, Paterson, Newark",
  recourseAdjustment: "Non-Recourse",
  baltimoreAdjustment:  "Baltimore",
  stabilizedVacancy: "Stabilized Refinance Vacancy",
  ficoBetween650and680: "FICO Between 650 and 680",
  hpaDeclineBetweenTwoToFivePercent: "HPA decline between 2% - 5%",
  loanSize: "Loan Size",
  noPlansAndPermitsApproval: "No Plans/Permits Approvals",
  totalMarketDataDeductions: "Total Market Data Deductions",
  medianHomeValueMultiplier: "Median Home Value Multiplier",
  condoEligibility: "Condo Eligibility Adjustment",
  loanBalance75KandDscr2x: "Loan Balance & DSCR",
  zipCodeDensity: "Zip Code Density",
  dscrAdjustment: "DSCR Adjustment",
  occupancyAdjustment: "Occupancy Adjustment",
  totalAdjustments: "Total Adjustments",
  loanPurposeAdjustment: "Cash Out",
  decliningMarketsAdjustment: "Declining Markets Adjustment",
  propertyLocationAdjustment: "Property Location Adjustment",
  costarVacancyRateAdjustment: "CoStar Vacancy Rate",
  costarPropertyRentAdjustment: "Property rent vs market adjustment",
  geographicAdjustment: "Geographic Adjustment",
};

export const finalPricingTables: ObjectType = {
  "basePrice": "basePrice",
  "actualInterestRate": "actualInterestRate", 
  "noteRate": "noteRate", 
  "prepaymentPenaltyMaxPrice": "pppMaxPrice",
  "stateRestrictionMaxPrice": "statepppRestrictionMaxPrice",
  "maxAllowedToorakPrice": "maxAllowedToorakPrice",
}